import { Check, Copy01, File06, Image01, Trash01, UploadCloud02 } from "@untitledui/icons";
import { useState } from "react";
import { FileTrigger as AriaFileTrigger } from "react-aria-components";
import { useLoaderData, useRevalidator } from "react-router";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Table, TableCard } from "@/components/application/table/table";
import { ConfirmButton } from "@/components/admin/confirm-button";
import { Deferred, Loading, Skeleton, TableSkeleton } from "@/components/admin/loading";
import { useToast } from "@/components/admin/toast";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { useClipboard } from "@/hooks/use-clipboard";
import { formatDate, formatSize, type Api, type Asset } from "../lib/api.js";
import { PageHeader } from "./shell.jsx";

const ACCEPTED = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".svg", ".ico", ".pdf"];

function CopyLink({ url }: { url: string }) {
  const { copied, copy } = useClipboard();
  return <ButtonUtility size="sm" color="tertiary" tooltip={copied ? "已复制" : "复制链接"} icon={copied ? Check : Copy01} onClick={() => void copy(url)} />;
}

const DESCRIPTION = "任何拿到链接的人都能访问。";

function AssetsTableSkeleton() {
  return (
    <Loading>
      <TableCard.Root>
        <TableSkeleton columns={["文件", "大小", "上传时间", ""]} rows={4} />
      </TableCard.Root>
    </Loading>
  );
}

export function AssetsSkeleton() {
  return (
    <>
      <PageHeader title="资源" description={DESCRIPTION} actions={<Skeleton className="h-10 w-24 rounded-lg" />} />
      <AssetsTableSkeleton />
    </>
  );
}

/** The platform's public files: app icons, odds and ends. Anyone who has a file's link can read it. */
export function AssetsRoute() {
  const { api, data } = useLoaderData<{ api: Api; data: Promise<Asset[]> }>();
  const toast = useToast();
  const revalidator = useRevalidator();
  const [uploading, setUploading] = useState(false);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) await api.uploadAsset(file);
      toast.success(files.length === 1 ? "已上传" : `已上传 ${files.length} 个文件`);
    } catch (err) {
      toast.error(err);
    } finally {
      setUploading(false);
      await revalidator.revalidate();
    }
  };

  return (
    <>
      <PageHeader
        title="资源"
        description={DESCRIPTION}
        actions={
          <AriaFileTrigger allowsMultiple acceptedFileTypes={ACCEPTED} onSelect={(files) => void upload(files)}>
            <Button iconLeading={UploadCloud02} isLoading={uploading} showTextWhileLoading>
              上传
            </Button>
          </AriaFileTrigger>
        }
      />
      <Deferred data={data} fallback={<AssetsTableSkeleton />}>
        {(assets) => <Assets api={api} data={assets} onChanged={() => revalidator.revalidate()} />}
      </Deferred>
    </>
  );
}

function Assets({ api, data, onChanged }: { api: Api; data: Asset[]; onChanged: () => Promise<void> }) {
  const toast = useToast();
  return (
    <>
      {data.length === 0 ? (
        <div className="rounded-xl bg-primary py-16 shadow-xs ring-1 ring-secondary">
          <EmptyState size="md">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon icon={Image01} color="gray" theme="modern" />
            </EmptyState.Header>
            <EmptyState.Content>
              <EmptyState.Title>没有资源</EmptyState.Title>
            </EmptyState.Content>
          </EmptyState>
        </div>
      ) : (
        <TableCard.Root>
          <Table aria-label="资源">
            <Table.Header>
              <Table.Head id="name" label="文件" isRowHeader />
              <Table.Head id="size" label="大小" />
              <Table.Head id="updatedAt" label="上传时间" />
              <Table.Head id="actions" />
            </Table.Header>
            <Table.Body items={data}>
              {(a) => (
                <Table.Row id={a.key}>
                  <Table.Cell>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="flex min-w-0 items-center gap-3 font-medium text-primary">
                      <span className="flex size-10 flex-none items-center justify-center overflow-hidden rounded-lg bg-secondary ring-1 ring-secondary ring-inset">
                        {a.contentType.startsWith("image/") ? <img src={a.url} alt="" className="max-h-full max-w-full object-contain" /> : <File06 className="size-5 text-quaternary" />}
                      </span>
                      <span className="truncate">{a.name}</span>
                    </a>
                  </Table.Cell>
                  <Table.Cell>{formatSize(a.size)}</Table.Cell>
                  <Table.Cell>{formatDate(a.updatedAt)}</Table.Cell>
                  <Table.Cell>
                    <div className="flex justify-end gap-1">
                      <CopyLink url={a.url} />
                      <ConfirmButton
                        title={`删除 ${a.name}？`}
                        description="用到这个链接的地方会失效。"
                        confirmLabel="删除"
                        destructive
                        onConfirm={async () => {
                          try {
                            await api.deleteAsset(a.key);
                            toast.success("已删除");
                          } catch (err) {
                            toast.error(err);
                          }
                          await onChanged();
                        }}
                        color="tertiary"
                        size="sm"
                        iconLeading={Trash01}
                        aria-label={`删除 ${a.name}`}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      )}
    </>
  );
}
